import { Accordion } from '@eleveo/components-react';
import React, { Key, useMemo, useState } from 'react';
import { useFormState } from 'react-final-form';
import { useTranslation } from 'react-i18next';

import { AdherenceData } from './adherence/AdherenceData';
import { HistoricalData } from './historicalData/HistoricalData';
import { MediaImport } from './mediaImport/MediaImport';
import { RealTimeData } from './realTimeData/RealTimeData';
import { UserSynchronization } from './userSync/UserSynchronization';
import { useIntegrations } from '../../../app/IntegrationsProvider';
import { TEST_IDS } from '../../../constants/dataTestIds';
import { createTaskName } from '../../../scene/integrations/config';
import { ConfigurationType, JOB_NAMES, STREAM_NAMES, TASK_NAMES } from '../../../scene/integrations/types';
import {
  getAdherenceState,
  getHistoricalDataState,
  getMediaImportState,
  getRealTimeDataState,
  getStreamFormOrder,
  getUserSynchronizationState,
} from '../../../utils/formUtils';
import { Values } from '../../configuration/types';

export interface IntegrationJobsProps {
  availableJobs: Values<typeof JOB_NAMES>[];
  availableStreams: Values<typeof STREAM_NAMES>[];
}

type IntegrationAccordionItemType = {
  Content: React.FC<any>;
  id: string;
  testId?: string;
  title: string;
  subtitle?: string;
  props?: any;
};

export const IntegrationJobs: React.FC<IntegrationJobsProps> = ({ availableJobs, availableStreams }) => {
  const { integrationType } = useIntegrations();

  const { t } = useTranslation();
  const [expandedJobs, setExpandedJobs] = useState<string[]>([]);
  const { values: formState } = useFormState<ConfigurationType>({ subscription: { values: true } });

  const {
    cronExpression: historicalDataCronExpression,
    taskProperties: { lookBackPeriod: historicalDataLookBackPeriod } = {},
  } = getHistoricalDataState(formState, integrationType);

  const { cronExpression: adherenceCronExpression, taskProperties: { lookBackPeriod: adherenceLookBackPeriod } = {} } =
    getAdherenceState(formState, integrationType);

  const { cronExpression: userSynchronizationCronExpression, scheduled: isUserSynchronizationEnabled } =
    getUserSynchronizationState(formState, integrationType);

  const { enabled: isRealTimeDataEnabled } = getRealTimeDataState(formState, integrationType);
  const { enabled: isMediaImportEnabled } = getMediaImportState(formState, integrationType);

  const accordionItems: IntegrationAccordionItemType[] = useMemo(() => {
    if (!availableJobs.length && !availableStreams.length) {
      return [];
    }

    const availableTasks = {
      [JOB_NAMES.userSynchronization]: {
        testId: TEST_IDS.accordionItemUserSynchronization,
        Content: UserSynchronization,
        subtitle: userSynchronizationCronExpression ? t('myConfigurations.tasks.filled') : undefined,
        title: t('myConfigurations.tasks.userSynchronization.title'),
        props: { expandedJobs },
      },
      [JOB_NAMES.adherence]: {
        testId: TEST_IDS.accordionItemAdherenceData,
        Content: AdherenceData,
        subtitle: adherenceCronExpression && !!adherenceLookBackPeriod ? t('myConfigurations.tasks.filled') : undefined,
        title: t('myConfigurations.tasks.adherence.title'),
      },
      [JOB_NAMES.historicalData]: {
        testId: TEST_IDS.accordionItemHistoricalData,
        Content: HistoricalData,
        subtitle:
          historicalDataCronExpression && !!historicalDataLookBackPeriod
            ? t('myConfigurations.tasks.filled')
            : undefined,
        title: t('myConfigurations.tasks.historicalData.title'),
      },
      [STREAM_NAMES.realTimeData]: {
        testId: TEST_IDS.accordionItemRealTimeData,
        Content: RealTimeData,
        props: { index: getStreamFormOrder(STREAM_NAMES.realTimeData, availableStreams) },
        subtitle: isRealTimeDataEnabled ? t('myConfigurations.tasks.filled') : undefined,
        title: t('myConfigurations.tasks.realTimeData.title'),
      },
      [STREAM_NAMES.mediaImport]: {
        testId: TEST_IDS.accordionItemMediaImport,
        Content: MediaImport,
        props: { index: getStreamFormOrder(STREAM_NAMES.mediaImport, availableStreams) },
        subtitle: isMediaImportEnabled ? t('myConfigurations.tasks.filled') : undefined,
        title: t('myConfigurations.tasks.mediaImport.title'),
      },
    };

    const tasksToReturn: IntegrationAccordionItemType[] = [];
    [...availableJobs, ...availableStreams].forEach((jobName) => {
      const taskName = createTaskName(jobName, integrationType);
      if (taskName) {
        tasksToReturn.push({
          id: taskName,
          ...availableTasks[jobName],
        });
      }
    });

    return tasksToReturn;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    availableJobs,
    availableStreams,
    expandedJobs,
    integrationType,
    isRealTimeDataEnabled,
    historicalDataCronExpression,
    historicalDataLookBackPeriod,
    adherenceCronExpression,
    adherenceLookBackPeriod,
    userSynchronizationCronExpression,
  ]);

  return (
    <Accordion
      className="mbs-20 -mli-20"
      expansionMode="single"
      items={accordionItems}
      disabledKeys={
        isUserSynchronizationEnabled
          ? []
          : [
              TASK_NAMES.adherence(integrationType),
              TASK_NAMES.historicalData(integrationType),
              TASK_NAMES.realTimeData(integrationType),
            ]
      }
      onExpandedChange={(selectedKeys: Set<Key>) => {
        setExpandedJobs(Array.from(selectedKeys.values()) as string[]);
      }}
    >
      {(Item) => <Item.Content props={Item.props} />}
    </Accordion>
  );
};
